import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

import BetHistory from 'src/components/Account/AccountHistory/BetHistory/BetHistory';
import Button from 'src/components/Button/Button';
import { Bet, Stake } from 'src/interfaces/BetslipState';
import './TicketDetails.scss';
import { formatAmountToFixed } from 'src/utils/utils';
import { isAfter } from 'date-fns/esm';
import ModalContainer from '../CancelTicket/components/ModalContainer/ModalContainer';

interface TicketDetailsProps {
  details: {
    bets: Bet[],
    stakes: {
      combinations: Stake[]
    },
    numberOfBets?: number,
    reprintCount?: number,
    id: number,
    isCancellable: boolean,
    cancellableUntil: number,
    isPayoutable: boolean,
    code: string,
    stake: number,
    ticketNumber: string,
    status: string,
    maximumProfit: number,
    cancelledAt: number,
    profit: number,
    maximumReturn: number,
    divisor: number,
    potentialNetReturn: number,
    customerTax: number,
    return: number,
    netReturn: number,
    registerDate: number,
    evaluationDate: number,
    totalStake: number,
    totalReturn: number,
    totalReturnedStake: number,
    pin: string,
    totalPotentialTax: number,
    paidOut: number,
    betType: number,
    totalWinning: number,
    toPay: number,
    cashOut: number,
    isCancelable?: boolean,
    statusName: string,
    settlingStatus: {
      totalBetsCount: number,
      settledBetsCount: number
    },
    tax: {
      taxType: string,
      taxPercentage: string
    }
  }
  setDetails: Function,
  isCancelable: boolean,
  isPayoutable:boolean,
  updateIsPayotable: Function,
  updateIsCancellable: Function,
  presentBetslip: Function
}

function TicketDetails({ details,
  setDetails = () => {},
  isPayoutable,
  isCancelable,
  updateIsCancellable,
  updateIsPayotable,
  presentBetslip
}: TicketDetailsProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [showPinModal, setShowPinModal] = useState('');

  const getTicketDetails = async () => {
    const newDetails = await dispatch.peripherals.getTicketDetails({ ticket: details.code });
    if (newDetails && _.size(newDetails) > 0){
      updateIsCancellable(newDetails.isCancellable && isAfter(new Date(), newDetails.cancellableUntil));
      updateIsPayotable(newDetails.statusName === 'settled' && newDetails.netReturn > 0 && newDetails.paidOut < newDetails.netReturn);
      await setDetails(newDetails);
    }
  };

  useEffect(() => {
    getTicketDetails();
  }, [history.location.hash]);

  const cancelTicketRequest = async () => {
    setShowPinModal('');
    history.push(`#cancel-ticket?&code=${ details.code }`);
  };

  const payTicket = async () => {
    setShowPinModal('');
    history.replace(`#payout-ticket?&code=${ details.code }&value=${ formatAmountToFixed(details.totalReturn, details.divisor, true) }`);
  };

  const addBetsToBetslip = async () => {
    await dispatch.peripherals.addBetsToBetslip(details.code);
    presentBetslip(true);
  };

  const getMatrix = async () => {
    const response = await dispatch.peripherals.getCardFromTicket(details.code);
    if (response){
      presentBetslip(true);
    }
  };

  const getPayoutReceipt = async () => {
    await dispatch.peripherals.payoutReceipt(details.code);
  };

  const showOnSecondScreen = () => {
    dispatch.peripherals.setTicketCode(details.code);
    dispatch.content.changeShowSecondScreen('ticket');
  };

  return (
    <>
      <div className="ticket-details">
        <div className="ticket-details__column">
          <h3 className="ticket-details__title">{ t('ticketDetails.ticketSummary') }</h3>
          <div>{ t('ticketDetails.registerAt') }: { format(details.registerDate * 1000, 'dd-MM-yyyy - HH:mm:ss') }</div>
          {
            details.evaluationDate &&
           <div>{ t('ticketDetails.evaluatedAt') }: { format(details.evaluationDate * 1000, 'dd-MM-yyyy - HH:mm:ss') }</div>
          }
          {
            details.cancelledAt &&
           <div>{ t('ticketDetails.cancelledAt') }: { format(details.cancelledAt * 1000, 'dd-MM-yyyy - HH:mm:ss') }</div>
          }
          <div className="ticket-details__reprint-container">
            { details.reprintCount && details.reprintCount > 0 &&  <div>{ t('ticketDetails.duplicate') }: { details.reprintCount } </div> }
            <div>{ t('ticketDetails.status') }: { details.statusName } </div>
          </div>
          <div>{ t('ticketDetails.stake') }: { formatAmountToFixed(details.totalStake, details.divisor, true) }</div>
          <div>{ t('ticketDetails.return') }: { formatAmountToFixed(details.totalReturn, details.divisor, true) }</div>
          { details.status !== 'open' && <div>{ t('ticketDetails.tax') }: { formatAmountToFixed(details.customerTax, details.divisor, true) }</div> }
          {
            details.tax && details.tax.taxType === 'win' && details.status === 'open' &&
            <div>
              { `${ t('ticketDetails.tax') }(${ details.tax.taxType } ${ details.tax.taxPercentage }%)` }:
              { formatAmountToFixed(details.customerTax, details.divisor, true) }
            </div>
          }
          <div>{ t('ticketDetails.netReturn') }: { formatAmountToFixed(details.netReturn, details.divisor, true) }</div>
          <div>{ t('ticketDetails.paid') }: { formatAmountToFixed(details.paidOut, details.divisor, true) } </div>
          <div className="ticket-details__button-container">
            {
              isPayoutable && <Button
                type="button"
                text={ t('retail.payout') }
                className="btn btn--outlined cancel-ticket__submit retail-home__button--revert-color"
                onClick={ details.reprintCount && details.reprintCount > 0 ? () => setShowPinModal('payout') : payTicket }
              />
            }
            {
              details.paidOut > 0 && <Button
                type="button"
                text={ t('retail.printReceipt') }
                className="btn btn--outlined cancel-ticket__submit retail-home__button--revert-color"
                onClick={ getPayoutReceipt }
              />
            }
            {
              (isCancelable && !details.cancelledAt) && <Button
                type="button"
                text={ t('home.cancelTicket') }
                className="btn btn--outlined cancel-ticket__submit retail-home__button--revert-color"
                onClick={ details.reprintCount && details.reprintCount > 0 ? () => setShowPinModal('cancel') : cancelTicketRequest }
              />
            }
            {
              details.bets.length === 1 && <Button
                type="button"
                text={ t('retail.convertToCard') }
                className="btn btn--outlined cancel-ticket__submit retail-home__button--revert-color"
                onClick={ getMatrix }
              />
            }
            <Button
              type="button"
              text={ t('retail.copyToBetslip') }
              className="btn btn--outlined cancel-ticket__submit retail-home__button--revert-color"
              onClick={ addBetsToBetslip }
            />
            <Button
              type="button"
              text={ t('retail.showOnSecondScreen') }
              className="btn btn--outlined cancel-ticket__submit retail-home__button--revert-color"
              onClick={ showOnSecondScreen }
            />
          </div>
        </div>
        <div className="ticket-details__column">
          <h3 className="ticket-details__title">{ t('ticketDetails.ticketDetails') }</h3>
          <BetHistory
            key={ details.ticketNumber }
            isTicketDetail
            myBets={ false }
            item={ details }
          />
        </div>
      </div>
      {
        showPinModal &&
        <ModalContainer onClose={ () => setShowPinModal('') }>
          <ModalContainer.Pin
            pin={ details.pin }
            onSubmit={ showPinModal==='payout' ? payTicket : cancelTicketRequest }
            onClose={ () => setShowPinModal('') }
            code={ details.code }
          />
        </ModalContainer>
      }
    </>
  );
}

export default TicketDetails;
